import { useCallback, useEffect, useRef, useState } from "react";
import { Plane, Plus } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { useApi, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { categoryLabel } from "@/lib/format";

export default function AircraftInput({ value, onChange, onSelect, category, className, testid = "option-aircraft_type-input", ...rest }) {
  const api = useApi();
  const { me } = useMe();
  const isAdmin = me?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const typed = useRef(false);
  const q = (value || "").trim();
  const exact = items.some((a) => a.name.toLowerCase() === q.toLowerCase());
  const canAdd = isAdmin && q.length >= 2 && !exact;

  const fetchItems = useCallback(async (term) => {
    try {
      const { data } = await api.get("/aircraft", { params: { q: term } });
      setItems(data); setActive(0); setOpen(true);
    } catch { setItems([]); }
  }, [api]);

  useEffect(() => {
    if (!typed.current) { return undefined; }
    const t = setTimeout(() => fetchItems(q), 150);
    return () => clearTimeout(t);
  }, [q, fetchItems]);

  const pick = (a) => { typed.current = false; onChange(a.name); onSelect?.(a); setOpen(false); };
  const add = async () => {
    try {
      const { data } = await api.post("/aircraft", { name: q, category: category || null });
      toast.success(`${data.name} added to the aircraft list`);
      pick(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const total = items.length + (canAdd ? 1 : 0);
  const onKey = (e) => {
    if (!open || !total) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (i + 1) % total); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (i - 1 + total) % total); }
    else if (e.key === "Enter") { e.preventDefault(); if (active < items.length) pick(items[active]); else add(); }
    else if (e.key === "Escape") setOpen(false);
  };
  const row = (i) => `flex items-center gap-2.5 px-3 py-2.5 cursor-pointer text-sm transition-colors ${i === active ? "bg-[#1a2540]" : "hover:bg-white/5"}`;

  return (
    <div className="relative">
      <Input {...rest} className={className} value={value} autoComplete="off" data-testid={testid}
        onChange={(e) => { typed.current = true; onChange(e.target.value); }} onKeyDown={onKey}
        onFocus={() => fetchItems(q)}
        onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {open && total > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-auto bg-[#0d0f14] border border-white/10 shadow-2xl" data-testid="aircraft-suggestions">
          {items.map((a, i) => (
            <li key={a.id} className={row(i)} onMouseDown={(e) => { e.preventDefault(); pick(a); }} onMouseEnter={() => setActive(i)} data-testid={`aircraft-option-${i}`}>
              <Plane size={13} className="shrink-0 text-[#5B7FD1]" /><span className="text-white truncate flex-1">{a.name}</span>
              {a.category && <span className="text-[11px] text-zinc-500 shrink-0" data-testid={`aircraft-option-category-${i}`}>{categoryLabel(a.category)}</span>}
            </li>
          ))}
          {canAdd && (
            <li className={`${row(items.length)} border-t border-white/10`} onMouseDown={(e) => { e.preventDefault(); add(); }} onMouseEnter={() => setActive(items.length)} data-testid="aircraft-add-to-list">
              <Plus size={13} className="shrink-0 text-[#5B7FD1]" /><span className="text-white">Add “{q}” to the aircraft list</span>
            </li>
          )}
        </ul>
      )}
      {!isAdmin && q.length >= 2 && !exact && open && (
        <p className="text-[11px] text-zinc-500 mt-1" data-testid="aircraft-unlisted-hint">Not on the list. It will be saved on this option only.</p>
      )}
    </div>
  );
}
